import { logger } from 'src/helpers/logger';
import { RATE_CARD_FINDING_SEVERITIES, RATE_CARD_SIDE_IDS, RATE_CARD_WAITING_RULES } from './constants';
import type { RateCardFindingSeverity, RateCardSideId } from './constants';
import type { IRateCardFinding, IRateCardValidation } from './types';

/**
 * An unrecognised severity is read as a blocker, and logged: it means this page is behind the server's
 * contract, which is worth knowing before a user reports a Confirm they cannot press.
 */
const toSeverity = (value: unknown): RateCardFindingSeverity => {
  const known = Object.values(RATE_CARD_FINDING_SEVERITIES).find(severity => severity === value);

  if (known) return known;

  logger.error(new Error(`rate card finding: unrecognised severity ${String(value)}`));

  return RATE_CARD_FINDING_SEVERITIES.BLOCKER;
};

// `null` is a real value — a card-level finding belongs to no side — so anything unrecognised is
// flattened to it rather than rendered as a side chip nobody can read.
const toSide = (value: unknown): RateCardSideId | null => RATE_CARD_SIDE_IDS.find(side => side === value) ?? null;

/**
 * Strings are coerced, because a row number serialised as one would otherwise be dropped and leave the
 * finding pointing at nothing. Rows are 1-based, so the bound also rejects what `Number(null)` gives.
 */
const toRows = (rows: unknown[]): number[] =>
  rows
    .map(row => (typeof row === 'number' || typeof row === 'string' ? Number(row) : NaN))
    .filter(row => Number.isInteger(row) && row > 0);

const toFinding = (raw: any): IRateCardFinding => ({
  rule: typeof raw?.rule === 'string' ? raw.rule : '',
  severity: toSeverity(raw?.severity),
  side: toSide(raw?.side),
  row_numbers: Array.isArray(raw?.row_numbers) ? toRows(raw.row_numbers) : [],
  message: raw?.message ?? '',
  fix_path: raw?.fix_path ?? '',
});

/**
 * The server's order is kept: blockers first, then card-level findings before pay before bill, then
 * rule, column and row. It knows which rule outranks which, and re-sorting here could only guess.
 */
export const toValidation = (raw: any): IRateCardValidation => {
  const findings: IRateCardFinding[] = Array.isArray(raw?.findings) ? raw.findings.map(toFinding) : [];

  return {
    // The stricter of the two answers. Derived from the findings so a heading, a button and a list
    // cannot disagree — and never more permissive than the server's own verdict.
    publishable:
      raw?.publishable !== false &&
      !findings.some(finding => finding.severity === RATE_CARD_FINDING_SEVERITIES.BLOCKER),
    findings,
  };
};

export const findingsOf = (
  validation: IRateCardValidation | null,
  severity: RateCardFindingSeverity,
): IRateCardFinding[] => (validation?.findings ?? []).filter(finding => finding.severity === severity);

/**
 * True when every blocker is one that clears itself — an upload still processing, rows still
 * resolving. Those call for another look rather than another file, so the page says so.
 */
export const isWaitingOnImport = (validation: IRateCardValidation | null): boolean => {
  const blockers = findingsOf(validation, RATE_CARD_FINDING_SEVERITIES.BLOCKER);

  return blockers.length > 0 && blockers.every(finding => RATE_CARD_WAITING_RULES.includes(finding.rule));
};
